// ═══════════════════════════════════════════════════════════════════════════════
// Android browser — Google Play install banner (not PWA add-to-home-screen)
// ═══════════════════════════════════════════════════════════════════════════════

import { APP_NAME, PLAY_STORE_LISTING_URL } from '@fdh/shared';
import { useCallback, useEffect, useState, type KeyboardEvent } from 'react';
import { useTranslationSafe } from '../contexts/LanguageContext';
import { isPlayStoreClient } from '../lib/clientChannel';
import { isAndroidDevice } from '../lib/deviceDetection';
import { APP_ICONS } from '../lib/iconAssets';
import { isRunningAsPwa } from '../lib/pwaRuntime';
import { appButtonClassName } from './ui/AppButton';

const DISMISS_STORAGE_KEY = 'fdh-play-store-banner-dismissed';
const PLAY_BANNER_HEIGHT_CSS = '3.5rem';
const PLAY_BANNER_HEIGHT_VAR = '--fdh-play-banner-h';

const readDismissed = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return window.localStorage.getItem(DISMISS_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const persistDismissed = (): void => {
  try {
    window.localStorage.setItem(DISMISS_STORAGE_KEY, 'true');
  } catch {
    // ignore localStorage write failures
  }
};

const setPlayBannerHeightVar = (height: string): void => {
  if (typeof document === 'undefined') {
    return;
  }
  document.documentElement.style.setProperty(PLAY_BANNER_HEIGHT_VAR, height);
};

export const PlayStoreInstallBanner = () => {
  const t = useTranslationSafe();
  const [dismissed, setDismissed] = useState(readDismissed);

  useEffect(() => {
    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  const isVisible =
    isAndroidDevice() && !isRunningAsPwa() && !isPlayStoreClient() && !dismissed;

  useEffect(() => {
    setPlayBannerHeightVar(isVisible ? PLAY_BANNER_HEIGHT_CSS : '0px');
    return () => {
      setPlayBannerHeightVar('0px');
    };
  }, [isVisible]);

  const handleDismiss = useCallback(() => {
    persistDismissed();
    setDismissed(true);
  }, []);

  const handleDismissKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleDismiss();
      }
    },
    [handleDismiss],
  );

  const handleInstallKeyDown = useCallback((event: KeyboardEvent<HTMLAnchorElement>) => {
    if (event.key === ' ') {
      event.preventDefault();
      event.currentTarget.click();
    }
  }, []);

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className="fixed left-0 right-0 top-0 z-30 border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900"
      role="region"
      aria-label={t('playBanner.regionAria')}
    >
      <div className="mx-auto flex h-14 max-w-lg items-center gap-2 px-2">
        <button
          type="button"
          onClick={handleDismiss}
          onKeyDown={handleDismissKeyDown}
          tabIndex={0}
          aria-label={t('playBanner.dismissAria')}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-gray-400 dark:hover:bg-gray-800"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="h-5 w-5"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <img
          src={APP_ICONS.any192}
          alt=""
          className="h-10 w-10 shrink-0 rounded-lg object-contain"
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-gray-900">{APP_NAME}</p>
          <p className="truncate text-xs text-gray-500">{t('playBanner.subtitle')}</p>
        </div>
        <a
          href={PLAY_STORE_LISTING_URL}
          target="_blank"
          rel="noopener noreferrer"
          tabIndex={0}
          aria-label={t('playBanner.installAria')}
          onKeyDown={handleInstallKeyDown}
          className={appButtonClassName({ variant: 'primary', size: 'md', className: 'shrink-0 px-3' })}
        >
          {t('playBanner.install')}
        </a>
      </div>
    </div>
  );
};
